import { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { css } from '@emotion/react';
import { EChartsOption } from 'echarts';
import { EChartWrapper } from '../../../components/Charts/EchartWrapper';
import { colors } from '../../../theme/colors';
import { panelSurfaceCss } from './panelStyles';

const panelCss = css`
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const headerCss = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const headerLeftCss = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const titleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
`;

const subtitleCss = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.secondary};
`;

const toggleGroupSx = {
  border: '1px solid #d5d7eb',
  borderRadius: '4px',
  height: 30,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    borderRight: '1px solid #d5d7eb !important',
    borderRadius: '0 !important',
    fontSize: 14,
    lineHeight: '24px',
    letterSpacing: '0.4px',
    padding: '3px 10px',
    color: colors.text.secondary,
    '&.Mui-selected': {
      background: colors.surface.selectedSubtle,
      color: colors.primary[60],
    },
    '&:last-of-type': {
      borderRight: 'none !important',
    },
  },
};

const chartContainerCss = css`
  height: 200px;
  width: 100%;
`;

type Tab = 'headcount' | 'webTraffic';

interface HeadcountEvolutionEntry {
  readonly stage: string;
  readonly headcount: number;
  readonly growthPct: number;
}

interface WebTrafficEvolutionEntry {
  readonly stage: string;
  readonly visits: number;
  readonly growthPct: number;
}

/**
 * Canonical per-tab entry consumed by `buildChartOption`. Both the
 * Headcount and Web Traffic tabs share the exact same chart shape (bar +
 * line on a dual y-axis), so the panel normalises both source arrays into
 * this common representation and lets the metadata table below drive the
 * axis / legend / tooltip labels.
 */
interface ChartEntry {
  readonly stage: string;
  readonly value: number;
  readonly growthPct: number;
}

interface TabMeta {
  readonly actualSeriesName: string;
  readonly actualUnit: string;
  readonly emptyStateText: string;
}

const TAB_META: Record<Tab, TabMeta> = {
  headcount: {
    actualSeriesName: 'Headcount Actual',
    actualUnit: 'employees',
    emptyStateText: 'No headcount growth data available',
  },
  webTraffic: {
    actualSeriesName: 'Monthly Visits',
    actualUnit: 'visits',
    emptyStateText: 'No web traffic growth data available',
  },
};

interface FundingEvolutionPanelProps {
  readonly data: readonly HeadcountEvolutionEntry[];
  /**
   * Web Traffic tab series. Optional — when absent, the Web Traffic tab
   * renders the empty state (the panel itself is rendered unconditionally
   * so users still see the "Headcount" tab controls).
   */
  readonly webTrafficData?: readonly WebTrafficEvolutionEntry[];
}

export function FundingEvolutionPanel({ data, webTrafficData }: FundingEvolutionPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>('headcount');

  const chartData: readonly ChartEntry[] =
    activeTab === 'headcount'
      ? data.map((d) => ({ stage: d.stage, value: d.headcount, growthPct: d.growthPct }))
      : (webTrafficData ?? []).map((d) => ({
          stage: d.stage,
          value: d.visits,
          growthPct: d.growthPct,
        }));

  return (
    <div css={[panelSurfaceCss, panelCss]}>
      <div css={headerCss}>
        <div css={headerLeftCss}>
          <Typography css={titleCss}>Growth Metrics</Typography>
          <Typography css={subtitleCss}>Actual vs growth rate across time windows</Typography>
        </div>
        <ToggleButtonGroup
          exclusive
          value={activeTab}
          onChange={(_e, val: Tab | null) => {
            if (val != null) {
              setActiveTab(val);
            }
          }}
          sx={toggleGroupSx}
        >
          <ToggleButton value='headcount'>Headcount</ToggleButton>
          <ToggleButton value='webTraffic'>Web Traffic</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div css={chartContainerCss}>
        <EChartWrapper options={buildChartOption(chartData, TAB_META[activeTab])} notMerge />
      </div>
    </div>
  );
}

function buildChartOption(data: readonly ChartEntry[], meta: TabMeta): EChartsOption {
  // Unified "no data" placeholder — reused by both tabs so an empty series
  // never renders as a blank panel with no context.
  if (data.length === 0) {
    return {
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: meta.emptyStateText,
            fontSize: 13,
            fill: colors.neutral[50],
          },
        },
      ],
    };
  }

  const timeWindows = data.map((d) => d.stage);
  const values = data.map((d) => d.value);
  const growths = data.map((d) => d.growthPct);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const window = items[0]?.name ?? '';
        const rows = items
          .map((item) => {
            const marker = item.marker ?? '';
            if (item.seriesName === meta.actualSeriesName) {
              return `${marker}${item.seriesName}: <b>${item.value} ${meta.actualUnit}</b>`;
            }
            const sign = Number(item.value) >= 0 ? '+' : '';
            return `${marker}${item.seriesName}: <b>${sign}${item.value}%</b>`;
          })
          .join('<br/>');
        return `<div style="font-size:12px"><b>Trailing ${window}</b><br/>${rows}</div>`;
      },
    },
    legend: {
      // Color affordance: both legend markers render as filled rectangles in
      // their series color. `icon: 'line'` produces a hairline that is
      // invisible at small sizes, so we force `rect` + `itemStyle.color` on
      // both entries to match the Figma swatch treatment.
      data: [
        { name: meta.actualSeriesName, icon: 'rect', itemStyle: { color: colors.primary[45] } },
        { name: 'Growth %', icon: 'rect', itemStyle: { color: colors.critical[50] } },
      ],
      bottom: 0,
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { fontSize: 12, color: colors.text.main },
    },
    // outerBoundsMode 'none' keeps the v5 grid layout (v6 reserves space for overflowing labels)
    grid: { top: 8, right: 52, bottom: 36, left: 48, outerBoundsMode: 'none' },
    xAxis: {
      type: 'category',
      data: timeWindows,
      axisLabel: { fontSize: 12, color: colors.neutral[60] },
      axisLine: { lineStyle: { color: colors.neutral[20] } },
      axisTick: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        axisLabel: { fontSize: 12, color: colors.neutral[60] },
        splitLine: { lineStyle: { color: colors.neutral[10] } },
      },
      {
        type: 'value',
        position: 'right',
        axisLabel: {
          show: true,
          fontSize: 12,
          color: colors.neutral[60],
          formatter: (v: number) => `${v}%`,
        },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    series: [
      {
        name: meta.actualSeriesName,
        type: 'bar',
        data: values,
        itemStyle: { color: colors.primary[45], borderRadius: [2, 2, 0, 0] },
        barMaxWidth: 80,
      },
      {
        name: 'Growth %',
        type: 'line',
        yAxisIndex: 1,
        data: growths,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: colors.critical[50], width: 2 },
        itemStyle: { color: colors.critical[50] },
        smooth: true,
      },
    ],
  };
}
